<script lang="ts" setup>
import SkrButton from '@/components/button/SkrButton.vue'
import SkrDivider from '@/components/SkrDivider.vue'
import SkrAutocomplete from '@/components/SkrAutocomplete.vue'
import SuspendedBadge from '@/components/business/members/SuspendedBadge.vue'

const props = defineProps<{
  members: string[]
  loading?: boolean
  title?: string
  subtitle?: string
  cancelText?: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'action', data: Dialogs['business/DialogAddMember']['contextData']): void
}>()

/**
 * The selected user IDs.
 */
const selection = ref<string[]>([])
const showDialog = ref<boolean>(true)
const isLoading = ref<boolean>(false)
const businessMembers = ref<MemberItem[]>([])
const { businessRepository } = useApi()
const { t } = useI18n()

const { id: businessId } = storeToRefs(useBusinessStore())

const fetchBusinessMembers = async () => {
  isLoading.value = true
  businessMembers.value = await businessRepository.getMembers(businessId.value)
  isLoading.value = false
}
const assignableMembers = computed(() =>
  businessMembers.value.filter(user => {
    return !props.members.some(member => member === user.email)
  })
)

const noDataText = computed(() => {
  return isLoading.value ? '' : t('business.seals.details.members_table.add_dialog.no_data')
})

const disableSuspendedMembers = (member: MemberItem) => ({ disabled: member.status === 'suspended' })

const addMembers = () => {
  const members = selection.value.reduce((acc: MemberItem[], user) => {
    const member = businessMembers.value.find(member => member.username === user)
    if (member) {
      acc.push(member)
    }
    return acc
  }, [])
  emit('action', members)
}

onMounted(() => {
  fetchBusinessMembers()
})
</script>

<template>
  <v-dialog v-model="showDialog" min-width="500" max-width="min-content" @click:outside="emit('close')">
    <v-card>
      <v-card-title data-cy="add_member_dialog">
        {{ title }}
      </v-card-title>
      <v-card-text>
        <p class="mb-4">
          {{ subtitle }}
        </p>
        <skr-autocomplete
          v-model="selection"
          :items="assignableMembers"
          item-title="email"
          item-value="username"
          :item-props="disableSuspendedMembers"
          :label="t('global.email')"
          :no-data-text="noDataText"
          multiple
          variant="filled"
          chips
          data-cy="add_member_input"
          :is-loading="isLoading"
        >
          <template #item="{ item, props: itemProps }">
            <v-list-item v-bind="itemProps">
              <template #prepend="{ isSelected }">
                <v-checkbox-btn
                  :model-value="isSelected"
                  :disabled="itemProps.disabled"
                  :ripple="false"
                  tabindex="-1"
                />
              </template>
              <template #title>
                <div class="member-option">
                  <span>{{ item.title }}</span>
                  <suspended-badge v-if="item.raw.status === 'suspended'" />
                </div>
              </template>
            </v-list-item>
          </template>
        </skr-autocomplete>
      </v-card-text>
      <skr-divider />
      <v-card-actions class="d-flex justify-space-between">
        <skr-button size="xl" color="info" type="secondary" @click="emit('close')">
          {{ cancelText || t('global.cancel') }}
        </skr-button>
        <skr-button
          size="xl"
          color="info"
          :loading="loading"
          :disabled="loading || selection.length === 0"
          data-cy="add_member_button"
          @click="addMembers"
        >
          {{ t('global.add') }}
        </skr-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.member-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
